<!-- Approved Content fills the tab: header and toolbar at their natural height, the table takes the
     rest and scrolls its own rows, and the selection bar sits pinned under it. Below 1060px the
     preview stacks under the table, so the tab scrolls and the table gets a fixed height. -->
<div class="mba @container flex h-full min-h-0 flex-col overflow-hidden font-(family-name:--font-fallback) text-(--fg-1) @max-[1060px]:overflow-y-auto">
  <div class="flex min-h-0 w-full flex-1 flex-col gap-4 @max-[1060px]:flex-none @max-[1060px]:pb-4">

    <!-- Section header -->
    <div class="flex shrink-0 flex-wrap items-end gap-3.5">
      <div class="flex min-w-0 flex-col gap-1.5">
        <h2 class="m-0 font-(family-name:--font-display) text-[30px] leading-[0.9] font-bold tracking-[-0.02em]">Approved Content</h2>
        <span class="text-[13px] text-(--fg-3)">
          @if (summary().count) {
          {{ summary().count }} video{{ summary().count === 1 ? '' : 's' }} from {{ summary().creators }} creator{{ summary().creators === 1 ? '' : 's' }} · {{ summary().size }}
          @if (summary().notDownloaded) {
          · <b class="text-(--app-accent-strong)">{{ summary().notDownloaded }} not downloaded yet</b>
          }
          } @else {
          Videos you approve land here, ready to download
          }
        </span>
      </div>
      <span class="flex-1"></span>
      @if (summary().count) {
      <button type="button" [class]="ui.btnPrimary + ' @max-[560px]:w-full @max-[560px]:justify-center'" (click)="openDownload({ kind: 'all' })">
        <mba-icon name="download" />Download all {{ summary().count }}
      </button>
      }
    </div>

    @if (loading() && !data()) {
    <!-- Loading -->
    <div [class]="ui.card + ' flex flex-col gap-3 p-4'" aria-busy="true" aria-label="Loading approved videos">
      @for (row of [1, 2, 3, 4]; track row) {
      <div class="flex items-center gap-3">
        <span class="h-14 w-9 animate-pulse rounded-[7px] bg-(--bg-alt)"></span>
        <span [class]="ui.skeleton + ' w-40'"></span>
        <span class="flex-1"></span>
        <span [class]="ui.skeleton + ' w-20'"></span>
      </div>
      }
    </div>
    } @else if (failed()) {
    <!-- Error -->
    <div [class]="ui.card + ' flex flex-col items-center gap-2 px-5 py-10 text-center'">
      <b class="font-(family-name:--font-display) text-[22px] font-extrabold">Couldn’t load approved videos</b>
      <span class="text-[13px] text-(--fg-3)">Check your connection and try again.</span>
      <button type="button" [class]="ui.btnOutline + ' mt-1'" (click)="retry()">Try again</button>
    </div>
    } @else if (!summary().count) {
    <!-- Empty -->
    <div [class]="ui.card + ' flex flex-col items-center gap-2 px-5 py-12 text-center'">
      <span class="mb-1.5 grid size-12 place-items-center rounded-full bg-(--app-accent-soft) text-(--app-accent)"><mba-icon name="film" size="size-[22px]" /></span>
      <b class="font-(family-name:--font-display) text-2xl leading-none font-extrabold">No Approved Videos Yet</b>
      <span class="max-w-[46ch] text-[13px] text-(--fg-3)">
        When you approve a creator’s video, it lands here ready to download.
        @if (hiredCount()) { {{ hiredCount() }} creator{{ hiredCount() === 1 ? ' is' : 's are' }} working on this brief right now. }
      </span>
      <button type="button" [class]="ui.btnPrimary + ' mt-1'" (click)="reviewVideos()"><mba-icon name="play-circle" />Review videos</button>
    </div>
    } @else {

    <!-- Toolbar -->
    <div class="flex shrink-0 flex-wrap items-center gap-2.5">
      <div class="inline-flex gap-0.5 rounded-full border border-(--border-subtle) bg-(--bg-alt) p-[3px]" role="group" aria-label="Download status">
        <button type="button" [class]="segClass(status() === 'all')" [attr.aria-pressed]="status() === 'all'" (click)="status.set('all')">
          All<span [class]="segCountClass(status() === 'all')">{{ statusCounts().all }}</span>
        </button>
        <button type="button" [class]="segClass(status() === 'new')" [attr.aria-pressed]="status() === 'new'" (click)="status.set('new')">
          Not downloaded yet<span [class]="segCountClass(status() === 'new')">{{ statusCounts().new }}</span>
        </button>
      </div>

      <label class="flex h-[38px] min-w-[200px] flex-[0_1_260px] items-center gap-2 rounded-full border border-(--border-subtle) bg-(--mba-surface) px-3 text-(--fg-3) transition-[border-color,box-shadow] duration-[140ms] focus-within:border-(--app-accent) focus-within:ring-[3px] focus-within:ring-(--app-accent-ring)">
        <mba-icon name="search" size="size-4" />
        <span class="sr-only">Search creators</span>
        <input type="search" placeholder="Search creators" [value]="query()" (input)="setQuery($event)"
          class="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-(--fg-1) shadow-none outline-none focus:ring-0" />
      </label>

      <span class="flex-1 @max-[760px]:hidden"></span>

      @if (deliverableOptions().items.length > 1) {
      <div class="inline-flex flex-wrap gap-0.5 rounded-full border border-(--border-subtle) bg-(--bg-alt) p-[3px]" role="group" aria-label="Deliverable">
        <button type="button" [class]="segClass(deliverable() === 'all')" [attr.aria-pressed]="deliverable() === 'all'" (click)="deliverable.set('all')">
          All videos<span [class]="segCountClass(deliverable() === 'all')">{{ deliverableOptions().all }}</span>
        </button>
        @for (option of deliverableOptions().items; track option.index) {
        <button type="button" [class]="segClass(deliverable() === option.index)" [attr.aria-pressed]="deliverable() === option.index" (click)="deliverable.set(option.index)">
          {{ option.label }}<span [class]="segCountClass(deliverable() === option.index)">{{ option.count }}</span>
        </button>
        }
      </div>
      }
    </div>

    <!-- Table + preview -->
    <div class="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_340px] grid-rows-[minmax(0,1fr)] gap-4 @max-[1060px]:flex-none @max-[1060px]:grid-cols-1 @max-[1060px]:grid-rows-none">
      <div class="flex min-h-0 min-w-0 flex-col gap-3 @max-[1060px]:h-[70vh]">
        <div [class]="ui.card + ' flex min-h-0 flex-1 flex-col overflow-hidden'">
          <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <table class="w-full border-collapse text-[13px]">
            <thead>
              <tr class="text-left text-[11px] font-bold uppercase tracking-[0.06em] text-(--fg-3)">
                <th class="w-11 sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-alt) py-2.5 pl-3">
                  <button type="button" role="checkbox" [class]="checkboxClass(headerCheck())"
                    [attr.aria-checked]="headerCheck() === 'on' ? 'true' : headerCheck() === 'mixed' ? 'mixed' : 'false'"
                    [attr.aria-label]="'Select all ' + visible().length" (click)="toggleVisible()">
                    @if (headerCheck() === 'on') { <mba-icon name="check" size="size-3.5" [strokeWidth]="3" /> }
                    @else if (headerCheck() === 'mixed') { <mba-icon name="minus" size="size-3.5" [strokeWidth]="3" /> }
                  </button>
                </th>
                <th class="sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-alt) px-3 py-2.5" [attr.aria-sort]="sort() === 'video' ? 'ascending' : null">
                  <button type="button" [class]="sortClass('video')" (click)="sort.set('video')">Video @if (sort() === 'video') { <mba-icon name="arrow-down" size="size-[13px]" /> }</button>
                </th>
                <th class="sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-alt) px-3 py-2.5" [attr.aria-sort]="sort() === 'creator' ? 'ascending' : null">
                  <button type="button" [class]="sortClass('creator')" (click)="sort.set('creator')">Creator @if (sort() === 'creator') { <mba-icon name="arrow-down" size="size-[13px]" /> }</button>
                </th>
                <th class="sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-alt) px-3 py-2.5 @max-[760px]:hidden">Length</th>
                <th class="sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-alt) px-3 py-2.5 @max-[760px]:hidden" [attr.aria-sort]="sort() === 'size' ? 'descending' : null">
                  <button type="button" [class]="sortClass('size')" (click)="sort.set('size')">Size @if (sort() === 'size') { <mba-icon name="arrow-down" size="size-[13px]" /> }</button>
                </th>
                <th class="sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-alt) px-3 py-2.5 @max-[560px]:hidden" [attr.aria-sort]="sort() === 'approved' ? 'descending' : null">
                  <button type="button" [class]="sortClass('approved')" (click)="sort.set('approved')">Approved @if (sort() === 'approved') { <mba-icon name="arrow-down" size="size-[13px]" /> }</button>
                </th>
                <th class="w-[52px] sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-alt) px-3 py-2.5"><span class="sr-only">Download</span></th>
              </tr>
            </thead>
            <tbody>
              @for (video of visible(); track video.id; let last = $last) {
              <tr [class]="rowClass(video)" (click)="preview(video.id)">
                <td [class]="'py-2.5 pl-3 ' + (last ? '' : 'border-b border-(--border-subtle)')">
                  <button type="button" role="checkbox" [class]="checkboxClass(isSelected(video.id) ? 'on' : 'off')"
                    [attr.aria-checked]="isSelected(video.id)"
                    [attr.aria-label]="'Select ' + shortCreatorName(video.creator) + ' ' + deliverableName(video.deliverableIndex)"
                    (click)="toggleOne(video.id, $event)">
                    @if (isSelected(video.id)) { <mba-icon name="check" size="size-3.5" [strokeWidth]="3" /> }
                  </button>
                </td>
                <td [class]="'px-3 py-2.5 ' + (last ? '' : 'border-b border-(--border-subtle)')">
                  <div class="flex min-w-0 items-center gap-3">
                    <span class="relative grid h-14 w-9 flex-none place-items-center overflow-hidden rounded-[7px] bg-(--app-grad) text-white">
                      @if (video.posterUrl) { <img [src]="video.posterUrl" alt="" class="absolute inset-0 size-full object-cover" loading="lazy" /> }
                      <span class="relative drop-shadow"><mba-icon name="play" size="size-[13px]" /></span>
                    </span>
                    <span class="flex min-w-0 flex-col gap-0.5">
                      <b class="font-(family-name:--font-display) text-[17px] leading-none font-bold text-(--fg-1)">{{ deliverableName(video.deliverableIndex) }}</b>
                      <span class="truncate text-xs text-(--fg-3)">{{ deliverableSub(video) }}</span>
                    </span>
                  </div>
                </td>
                <td [class]="'px-3 py-2.5 ' + (last ? '' : 'border-b border-(--border-subtle)')">
                  <span class="flex items-center gap-2 whitespace-nowrap">
                    <span class="grid size-[26px] shrink-0 place-items-center overflow-hidden rounded-full bg-(--app-accent-soft) font-(family-name:--font-display) text-xs font-extrabold text-(--app-accent-strong)">
                      @if (creatorPhoto(video); as photo) { <img [src]="photo" alt="" class="size-full object-cover" loading="lazy" /> }
                      @else { {{ creatorInitial(video.creator.name) }} }
                    </span>
                    <b class="font-(family-name:--font-display) text-base font-bold">{{ shortCreatorName(video.creator) }}</b>
                  </span>
                </td>
                <td [class]="'px-3 py-2.5 whitespace-nowrap text-(--fg-2) tabular-nums @max-[760px]:hidden ' + (last ? '' : 'border-b border-(--border-subtle)')">{{ formatDuration(video.durationSec) }}</td>
                <td [class]="'px-3 py-2.5 whitespace-nowrap text-(--fg-2) tabular-nums @max-[760px]:hidden ' + (last ? '' : 'border-b border-(--border-subtle)')">{{ formatBytes(video.fileSizeBytes) }}</td>
                <td [class]="'px-3 py-2.5 @max-[560px]:hidden ' + (last ? '' : 'border-b border-(--border-subtle)')">
                  <div class="flex flex-col items-start gap-[3px]">
                    <span class="whitespace-nowrap text-(--fg-2) tabular-nums" [attr.title]="video.approvedAtIsEstimate ? 'Upload date (approved before approval dates were recorded)' : null">{{ formatApprovedDate(video.approvedAt) }}</span>
                    @if (video.downloadedByMe) {
                    <span class="inline-flex items-center gap-1 text-xs whitespace-nowrap text-(--fg-3)"><mba-icon name="check" size="size-[13px]" />Downloaded</span>
                    } @else {
                    <span [class]="ui.newCreatorPill">New</span>
                    }
                  </div>
                </td>
                <td [class]="'px-3 py-2.5 text-right ' + (last ? '' : 'border-b border-(--border-subtle)')">
                  <button type="button" title="Download"
                    class="inline-grid size-[34px] cursor-pointer place-items-center rounded-[10px] border border-(--border-subtle) bg-(--mba-surface) text-(--fg-2) transition-colors duration-[140ms] hover:border-transparent hover:bg-(--app-accent-soft) hover:text-(--app-accent-strong) active:scale-[0.96] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-(--app-accent-ring)"
                    [attr.aria-label]="'Download ' + shortCreatorName(video.creator) + ' ' + deliverableName(video.deliverableIndex)"
                    (click)="openDownload({ kind: 'one', id: video.id }, $event)">
                    <mba-icon name="download" size="size-4" />
                  </button>
                </td>
              </tr>
              } @empty {
              <tr>
                <td colspan="7">
                  <div class="flex flex-col items-center gap-2 px-5 py-10 text-center">
                    <b class="font-(family-name:--font-display) text-[22px] font-extrabold">No videos match</b>
                    <span class="text-[13px] text-(--fg-3)">{{ status() === 'new' ? 'You’ve downloaded everything here. ' : '' }}Try a different filter.</span>
                    <button type="button" [class]="ui.btnOutline" (click)="clearFilters()">Clear filters</button>
                  </div>
                </td>
              </tr>
              }
            </tbody>
          </table>
          </div>
        </div>

        @if (selectedVideos().length) {
        <div role="region" aria-label="Selection"
          class="flex shrink-0 flex-wrap items-center gap-3 rounded-xl bg-(--shout-deep-purple) py-2.5 pr-3 pl-[18px] text-white animate-[mba-in_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none">
          <b class="font-(family-name:--font-display) text-xl leading-none font-extrabold">{{ selectedVideos().length }} selected</b>
          <span class="text-[13px] text-white/72">{{ selectedSize() }}</span>
          <button type="button" class="cursor-pointer text-[13px] font-bold text-[#F7B6EC] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60" (click)="clearSelection()">Clear</button>
          <span class="flex-1"></span>
          <button type="button" [class]="ui.btnPrimary" (click)="openDownload({ kind: 'selection' })">
            <mba-icon name="download" />Download {{ selectedVideos().length === 1 ? 'video' : selectedVideos().length + ' videos' }}
          </button>
        </div>
        }
      </div>

      <!-- Preview -->
      <aside [class]="ui.card + ' flex h-full min-h-0 flex-col overflow-y-auto @max-[1060px]:h-auto'" aria-label="Preview">
        <div class="flex items-center gap-2.5 border-b border-(--border-subtle) px-3.5 py-3">
          <span [class]="ui.kicker + ' flex-1'">Preview</span>
          <button type="button" [class]="ui.iconButton + ' disabled:opacity-40'" aria-label="Previous video" [disabled]="currentPosition() <= 0" (click)="step(-1)"><mba-icon name="chevron-up" /></button>
          <button type="button" [class]="ui.iconButton + ' disabled:opacity-40'" aria-label="Next video" [disabled]="currentPosition() < 0 || currentPosition() >= visible().length - 1" (click)="step(1)"><mba-icon name="chevron-down" /></button>
        </div>
        @if (current(); as video) {
        <!-- Player at the top; creator, details and Download pushed to the bottom of the card. -->
        <div class="flex min-h-0 flex-1 flex-col @max-[1060px]:flex-none @max-[1060px]:flex-row @max-[1060px]:items-start @max-[1060px]:gap-4 @max-[1060px]:p-4 @max-[560px]:flex-col">
          <!-- The player gets all the card's spare height, inset by p-4, and fits the video inside it.
               On very short screens it keeps 180px and the card scrolls instead. -->
          <mbt-approved-player class="min-h-[180px] flex-1 p-4 @max-[1060px]:h-[356px] @max-[1060px]:w-[200px] @max-[1060px]:flex-none @max-[1060px]:p-0"
            [src]="video.playbackUrl" [isHls]="video.isHls" [poster]="video.posterUrl" [aspectRatio]="aspectRatio(video)"
            [caption]="deliverableName(video.deliverableIndex)" [formatLabel]="formatLabel(video)" [durationSec]="video.durationSec" />
          <div class="flex min-w-0 shrink-0 flex-col gap-3.5 px-4 pb-4 @max-[1060px]:flex-1 @max-[1060px]:p-0">
            <div class="flex items-center gap-2.5">
              <span class="grid size-9 shrink-0 place-items-center overflow-hidden rounded-full bg-(--app-accent-soft) font-(family-name:--font-display) text-sm font-extrabold text-(--app-accent-strong)">
                @if (creatorPhoto(video); as photo) { <img [src]="photo" alt="" class="size-full object-cover" /> }
                @else { {{ creatorInitial(video.creator.name) }} }
              </span>
              <span class="flex min-w-0 flex-col gap-0.5">
                <b class="font-(family-name:--font-display) text-xl leading-none font-bold">{{ shortCreatorName(video.creator) }}</b>
                <span class="truncate text-xs text-(--fg-3)">{{ deliverableName(video.deliverableIndex) }} · {{ deliverableSub(video) }}</span>
              </span>
            </div>
            <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-[7px] text-[13px]">
              <dt class="text-(--fg-3)">Approved</dt>
              <dd class="m-0 font-semibold">{{ formatApprovedDate(video.approvedAt) }}{{ video.approvedByName ? ' by ' + video.approvedByName : '' }}</dd>
              <dt class="text-(--fg-3)">Format</dt>
              <dd class="m-0 font-semibold">{{ formatLabel(video) }} · {{ formatDuration(video.durationSec) }}</dd>
              <dt class="text-(--fg-3)">Final video</dt>
              <dd class="m-0 font-semibold">{{ formatBytes(video.fileSizeBytes) }}</dd>
              <dt class="text-(--fg-3)">Downloaded</dt>
              <dd class="m-0 font-semibold">{{ video.downloadedByMe ? 'Yes' : 'Not yet' }}</dd>
            </dl>
            <button type="button" [class]="ui.btnPrimary + ' w-full justify-center'" (click)="openDownload({ kind: 'one', id: video.id })">
              <mba-icon name="download" />Download video
            </button>
          </div>
        </div>
        } @else {
        <div class="px-5 py-10 text-center text-[13px] text-(--fg-3)">Pick a video to preview it here</div>
        }
      </aside>
    </div>
    }
  </div>

  @if (modal() && modalVideos().length) {
  <mbt-download-modal [videos]="modalVideos()" [title]="modalTitle()" [root]="downloadRoot()"
    [includeSheet]="includeSheet()" [layout]="layout()" [sheetBytes]="data()?.contentSheetBytes ?? 0"
    [maxBytes]="data()?.maxDownloadBytes ?? 800000000" [busy]="downloading()"
    (includeSheetChange)="includeSheet.set($event)" (layoutChange)="layout.set($event)"
    (closed)="closeModal()" (confirmed)="confirmDownload()" />
  }
</div>
